import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  FileText,
  ShieldCheck,
  Globe,
  CheckSquare,
  LogOut,
  ExternalLink,
  Mail,
  Copy,
  Check
} from 'lucide-react';
import CircularProgress from '../components/CircularProgress';
import Footer from '../components/Footer';
import Wordmark from '../components/Wordmark';
import { useAuth } from '../context/AuthContext';
import { trackFunnel } from '../lib/funnel';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Return a human-friendly relative time like "just now", "3 minutes ago",
 * "2 hours ago", "5 days ago". Returns "Not yet viewed" when ts is null.
 */
const formatRelativeTime = (ts) => {
  if (!ts) return 'Not yet viewed';
  const then = new Date(ts).getTime();
  const now = Date.now();
  const seconds = Math.max(0, Math.floor((now - then) / 1000));

  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [stats, setStats] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  // Re-render tick so "X minutes ago" stays live without refetching
  const [, setNowTick] = useState(Date.now());

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin', { replace: true });
      return;
    }
    trackFunnel('dashboard_accessed');
    fetchStats();
    fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const fetchBilling = async () => {
    try {
      const res = await axios.get(`${API}/billing/status`);
      setBilling(res.data);
    } catch {
      // non-fatal — leave billing as null and we'll fall back to a neutral label
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          'Could not load dashboard. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const copyTrustLink = async () => {
    if (!stats?.trust_id) return;
    const url = `${window.location.origin}/trust/${stats.trust_id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // noop
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-[#1a1a1a]/60">Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="border-b border-[#e0ddd8]">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Wordmark
            data-testid="dashboard-error-logo"
          />
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#e0ddd8]"
              data-testid="dashboard-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </Button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <p className="text-xl text-[#1a1a1a] mb-2" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Something went wrong
            </p>
            <p className="text-[#1a1a1a]/60 mb-6">{error}</p>
            <Button
              onClick={() => navigate('/wizard')}
              className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]"
            >
              Take the wizard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const tasksLabel =
    stats.tasks_total > 0
      ? `${stats.tasks_completed} / ${stats.tasks_total}`
      : '—';

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="dashboard-page">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Wordmark />
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden sm:inline text-sm text-[#1a1a1a]/60 truncate max-w-[180px]">
              {user?.email}
            </span>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#e0ddd8] h-11 px-3 sm:px-4"
              data-testid="dashboard-logout-button"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full flex-1">
        {/* Greeting */}
        <div className="mb-8 sm:mb-10">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-2 leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
          </h1>
          <p className="text-sm sm:text-base text-[#1a1a1a]/60">
            {stats.company_name ? `${stats.company_name} · ` : ''}
            Here's your compliance posture at a glance.
          </p>
        </div>

        {/* Score + Quick Stats */}
        <div className="grid lg:grid-cols-3 gap-5 sm:gap-6 mb-8 sm:mb-10">
          {/* Compliance Score */}
          <div className="lg:col-span-1 bg-[#f5f3ef] border border-[#e0ddd8] rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center">
            <h2
              className="text-lg sm:text-xl font-normal mb-3 sm:mb-4 text-center"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Compliance score
            </h2>
            <CircularProgress value={stats.compliance_score || 0} size={140} strokeWidth={10} />
            <p className="text-xs sm:text-sm text-[#1a1a1a]/60 mt-3 sm:mt-4 text-center">
              Complete your monthly checklist to improve your score.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div
              data-testid="dashboard-stat-policies"
              className="bg-white border border-[#e0ddd8] rounded-2xl p-5 sm:p-6"
            >
              <p className="text-sm text-[#1a1a1a]/60 mb-2">Policies live</p>
              <p
                className="text-4xl sm:text-5xl font-normal text-[#1a1a1a]"
                style={{ fontFamily: 'Instrument Serif, serif' }}
              >
                {stats.policies_count}
              </p>
              <p className="text-xs text-[#1a1a1a]/50 mt-3">
                Data handling, access control & incident response
              </p>
            </div>

            <div
              data-testid="dashboard-stat-tasks"
              className="bg-white border border-[#e0ddd8] rounded-2xl p-5 sm:p-6"
            >
              <p className="text-sm text-[#1a1a1a]/60 mb-2">Monthly tasks</p>
              <p
                className="text-4xl sm:text-5xl font-normal text-[#1a1a1a]"
                style={{ fontFamily: 'Instrument Serif, serif' }}
              >
                {tasksLabel}
              </p>
              <p className="text-xs text-[#1a1a1a]/50 mt-3">
                {stats.tasks_total > 0
                  ? `${stats.tasks_total - stats.tasks_completed} remaining this month`
                  : 'No tasks yet'}
              </p>
            </div>

            <div
              data-testid="dashboard-stat-trust-views"
              className="bg-white border border-[#e0ddd8] rounded-2xl p-5 sm:p-6"
            >
              <p className="text-sm text-[#1a1a1a]/60 mb-2">Trust page views</p>
              <p
                className="text-4xl sm:text-5xl font-normal text-[#1a1a1a]"
                style={{ fontFamily: 'Instrument Serif, serif' }}
              >
                {stats.trust_views}
              </p>
              <div
                className="mt-3 inline-flex items-center gap-1.5 text-xs"
                data-testid="dashboard-stat-trust-views-last-seen"
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    stats.last_viewed_at ? 'bg-[#0a5c44]' : 'bg-[#1a1a1a]/30'
                  }`}
                ></span>
                <span className="text-[#1a1a1a]/60">
                  {stats.last_viewed_at
                    ? `Last viewed ${formatRelativeTime(stats.last_viewed_at)}`
                    : 'Not yet viewed'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Cards */}
        <h2
          className="text-2xl sm:text-3xl font-normal mb-5 sm:mb-6 leading-tight"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          Your compliance package
        </h2>
        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 mb-10 sm:mb-12">
          <Link
            to={`/results/${stats.result_id}`}
            data-testid="dashboard-card-roadmap"
            className="bg-white border border-[#e0ddd8] rounded-2xl p-6 hover:border-[#0a5c44] transition-colors group"
          >
            <FileText className="w-8 h-8 text-[#0a5c44] mb-4" />
            <h3
              className="text-xl sm:text-2xl font-normal mb-2 group-hover:underline leading-snug"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Compliance roadmap
            </h3>
            <p className="text-[#1a1a1a]/70 text-sm mb-4">
              Your prioritised action items to reach a professional security posture.
            </p>
            <span className="inline-flex items-center gap-1 text-sm text-[#0a5c44]">
              Open roadmap <ExternalLink className="w-3 h-3" />
            </span>
          </Link>

          <Link
            to={`/results/${stats.result_id}`}
            data-testid="dashboard-card-policies"
            className="bg-white border border-[#e0ddd8] rounded-2xl p-6 hover:border-[#0a5c44] transition-colors group"
          >
            <ShieldCheck className="w-8 h-8 text-[#0a5c44] mb-4" />
            <h3
              className="text-xl sm:text-2xl font-normal mb-2 group-hover:underline leading-snug"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Policy templates
            </h3>
            <p className="text-[#1a1a1a]/70 text-sm mb-4">
              Data handling, access control, and incident response policies tailored to you.
            </p>
            <span className="inline-flex items-center gap-1 text-sm text-[#0a5c44]">
              View policies <ExternalLink className="w-3 h-3" />
            </span>
          </Link>

          <div
            data-testid="dashboard-card-trust-page"
            className="bg-white border border-[#e0ddd8] rounded-2xl p-6"
          >
            <Globe className="w-8 h-8 text-[#0a5c44] mb-4" />
            <h3
              className="text-xl sm:text-2xl font-normal mb-2 leading-snug"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Public trust page
            </h3>
            <p className="text-[#1a1a1a]/70 text-sm mb-4">
              Share this link with prospects to demonstrate your security posture.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link
                to={`/trust/${stats.trust_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-[#0a5c44] hover:underline"
              >
                Open <ExternalLink className="w-3 h-3" />
              </Link>
              <button
                onClick={copyTrustLink}
                data-testid="dashboard-copy-trust-link"
                className="inline-flex items-center gap-1 text-sm text-[#1a1a1a]/70 hover:text-[#1a1a1a]"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" /> Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" /> Copy link
                  </>
                )}
              </button>
            </div>
          </div>

          <Link
            to={`/results/${stats.result_id}`}
            data-testid="dashboard-card-monthly-tasks"
            state={{ tab: 'monthly_tasks' }}
            className="bg-white border border-[#e0ddd8] rounded-2xl p-6 hover:border-[#0a5c44] transition-colors group"
          >
            <CheckSquare className="w-8 h-8 text-[#0a5c44] mb-4" />
            <h3
              className="text-xl sm:text-2xl font-normal mb-2 group-hover:underline leading-snug"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              This month's tasks
            </h3>
            <p className="text-[#1a1a1a]/70 text-sm mb-4">
              {tasksLabel} completed. Keep going to improve your compliance score.
            </p>
            <span className="inline-flex items-center gap-1 text-sm text-[#0a5c44]">
              View tasks <ExternalLink className="w-3 h-3" />
            </span>
          </Link>
        </div>

        {/* Account Section */}
        <h2
          className="text-2xl sm:text-3xl font-normal mb-5 sm:mb-6 leading-tight"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          Account
        </h2>
        <div
          data-testid="dashboard-account-section"
          className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-2xl p-6 sm:p-8 mb-10 sm:mb-12"
        >
          <div className="grid sm:grid-cols-3 gap-5 sm:gap-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-[#1a1a1a]/50 mb-1">Email</p>
              <p className="text-[#1a1a1a]" data-testid="dashboard-account-email">
                {user?.email}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#1a1a1a]/50 mb-1">Company</p>
              <p className="text-[#1a1a1a]">{stats.company_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#1a1a1a]/50 mb-1">Subscription</p>
              {(() => {
                const status = billing?.subscription_status || 'inactive';
                const meta = {
                  active:    { label: 'Active',    color: '#0a5c44', dot: 'bg-[#0a5c44]' },
                  canceling: { label: 'Cancels at period end', color: '#a16207', dot: 'bg-yellow-500' },
                  pending:   { label: 'Processing payment', color: '#a16207', dot: 'bg-yellow-500' },
                  past_due:  { label: 'Past due',  color: '#a16207', dot: 'bg-yellow-500' },
                  canceled:  { label: 'Canceled',  color: '#991b1b', dot: 'bg-red-500' },
                  inactive:  { label: 'Inactive',  color: '#6b7280', dot: 'bg-gray-400' },
                }[status] || { label: status, color: '#6b7280', dot: 'bg-gray-400' };
                return (
                  <p
                    data-testid="dashboard-subscription-status"
                    data-status={status}
                    className="inline-flex items-center gap-2 font-medium"
                    style={{ color: meta.color }}
                  >
                    <span className={`w-2 h-2 rounded-full ${meta.dot}`}></span>
                    {meta.label}
                  </p>
                );
              })()}
              {billing?.subscription_cancels_at && (
                <p
                  data-testid="dashboard-cancels-at"
                  className="text-xs text-[#1a1a1a]/55 mt-1.5"
                >
                  Access ends{' '}
                  {new Date(billing.subscription_cancels_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </p>
              )}
            </div>
          </div>
          <CancelSubscriptionRow billing={billing} onCanceled={fetchBilling} />
        </div>
      </div>

      <Footer />
    </div>
  );
};

/* -------------------- Cancel subscription row -------------------- */

const CancelSubscriptionRow = ({ billing, onCanceled }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const status = billing?.subscription_status;
  const hasSub = !!billing?.subscription_id;
  const alreadyCanceling = status === 'canceling' || status === 'canceled';

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/billing/cancel-subscription`, {
        effective_from: 'next_billing_period',
      });
      setDone(true);
      if (onCanceled) await onCanceled();
      setTimeout(() => setOpen(false), 1500);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          'Could not cancel subscription. Please try again or contact support.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t border-[#e0ddd8] mt-6 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p className="text-sm text-[#1a1a1a]/60">
        {alreadyCanceling
          ? 'Your subscription is set to end at the next billing date. You\'ll keep access until then.'
          : 'Cancel anytime. Your access continues until the end of your current billing period.'}
      </p>
      {hasSub && !alreadyCanceling && (
        <button
          type="button"
          onClick={() => { setOpen(true); setError(''); setDone(false); }}
          data-testid="dashboard-cancel-subscription-button"
          className="inline-flex items-center gap-2 text-sm text-[#991b1b] hover:underline self-start sm:self-auto"
        >
          Cancel subscription
        </button>
      )}

      {open && (
        <div
          data-testid="cancel-subscription-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !submitting && setOpen(false)}
        >
          <div
            className="bg-white border border-[#e0ddd8] rounded-2xl shadow-xl w-full max-w-md p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="text-2xl font-normal mb-2 leading-tight"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Cancel your subscription?
            </h3>
            <p className="text-sm text-[#1a1a1a]/70 leading-relaxed mb-5">
              You'll keep full access — dashboard, Trust Page, and policies — until the end of your current billing period. After that your account becomes inactive and your Trust Page is hidden from the public.
            </p>

            {error && (
              <div
                data-testid="cancel-subscription-error"
                className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg text-sm mb-4"
              >
                {error}
              </div>
            )}
            {done && (
              <div
                data-testid="cancel-subscription-success"
                className="bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 rounded-lg text-sm mb-4"
              >
                Subscription scheduled to end at the next billing date.
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="cancel-subscription-keep-button"
                className="flex-1 h-10 rounded-md border border-[#e0ddd8] text-sm text-[#1a1a1a] hover:bg-[#f5f3ef] transition-colors disabled:opacity-50"
              >
                Keep subscription
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting || done}
                data-testid="cancel-subscription-confirm-button"
                className="flex-1 h-10 rounded-md bg-[#991b1b] text-white hover:bg-[#7f1d1d] transition-colors text-sm font-medium disabled:opacity-50"
              >
                {submitting ? 'Cancelling...' : done ? 'Cancelled' : 'Cancel subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
